<template>
    <Modal
        :open="open"
        title="角色管理"
        width="600px"
        :confirm-loading="confirmLoading"
        @update:open="$emit('update:open', $event)"
        @ok="handleOk"
        @cancel="handleCancel"
    >
        <div v-if="currentUser" class="role-management-content">
            <div class="user-info">
                <p>
                    <strong>管理員：</strong>
                    {{ currentUser.name }} ({{ currentUser.email }})
                </p>
            </div>

            <a-divider />

            <div class="roles-section">
                <h4>選擇角色</h4>
                <a-checkbox-group
                    v-model:value="selectedRoleIds"
                    style="width: 100%"
                    class="vaporwave-checkbox-group"
                >
                    <a-row :gutter="[16, 16]">
                        <a-col
                            v-for="role in allRoles"
                            :key="role.id"
                            :span="12"
                        >
                            <a-checkbox :value="role.id" class="role-checkbox">
                                {{ role.name }}
                            </a-checkbox>
                        </a-col>
                    </a-row>
                </a-checkbox-group>
            </div>
        </div>
    </Modal>
</template>

<script setup>
import { ref, watch } from 'vue';
import Modal from './Modal.vue';

// Props
const props = defineProps({
    open: {
        type: Boolean,
        default: false
    },
    currentUser: {
        type: Object,
        default: null
    },
    allRoles: {
        type: Array,
        default: () => []
    },
    selectedRoles: {
        type: Array,
        default: () => []
    },
    confirmLoading: {
        type: Boolean,
        default: false
    }
});

// Emits
const emit = defineEmits(['update:open', 'ok', 'cancel']);

// Reactive data
const selectedRoleIds = ref([]);

// Watch for changes in selectedRoles prop
watch(
    () => props.selectedRoles,
    (newRoles) => {
        selectedRoleIds.value = newRoles.map((role) => role.id);
    },
    { immediate: true }
);

// Methods
const handleOk = () => {
    emit('ok', selectedRoleIds.value);
};

const handleCancel = () => {
    emit('cancel');
};
</script>

<style scoped>
.role-management-content {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.user-info {
    margin-bottom: 16px;
}

.user-info p {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-size: 16px;
    margin: 0;
}

.user-info strong {
    color: #2a2a3a; /* 加深標籤顏色提高對比 */
    font-weight: 700;
}

.roles-section h4 {
    color: #2a2a3a; /* 加深小節標題 */
    font-family: 'Courier New', monospace;
    font-weight: 700;
    margin-bottom: 16px;
    text-shadow: none; /* 移除發光以提升可讀性 */
    font-size: 18px;
}

/* Checkbox Group 樣式 */
.vaporwave-checkbox-group :deep(.ant-checkbox-wrapper) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-size: 14px !important;
    margin-bottom: 12px !important;
    padding: 8px 12px !important;
    background: transparent !important; /* 去除背景色 */
    border: 1px solid #2a4a7a !important; /* 紫色框 */
    border-radius: 8px !important;
    transition: all 0.2s ease !important;
    width: 100% !important;
    display: flex !important;
    align-items: center !important;
}

/* 兜底：任何容器都不應帶有綠色背景 */
.vaporwave-checkbox-group,
.roles-section :deep(.ant-row),
.roles-section :deep(.ant-col),
.vaporwave-checkbox-group :deep(label.ant-checkbox-wrapper) {
    background: transparent !important;
}

.vaporwave-checkbox-group :deep(.ant-checkbox-wrapper:hover) {
    background: transparent !important;
    border-color: #2a4a7a !important;
    box-shadow: 0 0 8px rgba(42, 74, 122, 0.25) !important;
}

.vaporwave-checkbox-group :deep(.ant-checkbox-wrapper-checked) {
    background: transparent !important; /* 勾選也不填色 */
    border-color: #2a4a7a !important;
    box-shadow: 0 0 8px rgba(42, 74, 122, 0.25) !important;
}

.vaporwave-checkbox-group :deep(.ant-checkbox) {
    margin-right: 10px !important;
    top: 0 !important;
}

.vaporwave-checkbox-group :deep(.ant-checkbox-inner) {
    background: #ffffff !important;
    border: 2px solid #245a8c !important;
    border-radius: 4px !important;
    width: 18px !important;
    height: 18px !important;
    box-shadow: 0 0 0 1px rgba(36, 90, 140, 0.15) !important;
}

.vaporwave-checkbox-group :deep(.ant-checkbox:hover .ant-checkbox-inner) {
    border-color: #1a4268 !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.35) !important;
}

.vaporwave-checkbox-group :deep(.ant-checkbox-checked .ant-checkbox-inner) {
    background: #245a8c !important;
    border-color: #1a4268 !important;
}

.vaporwave-checkbox-group
    :deep(.ant-checkbox-checked .ant-checkbox-inner::after) {
    border-color: #ffffff !important;
    border-width: 0 2px 2px 0 !important;
    width: 5px !important;
    height: 10px !important;
    top: 46% !important;
    left: 26% !important;
    opacity: 1 !important;
}

/* Divider 樣式 */
:deep(.ant-divider) {
    border-color: rgba(168, 255, 246, 0.3) !important;
    margin: 20px 0 !important;
}

/* Row 與 Col 間距：兩個筐中間留間隔 */
:deep(.ant-row) {
    margin-left: -8px !important;
    margin-right: -8px !important;
}

:deep(.ant-col) {
    padding-left: 8px !important;
    padding-right: 8px !important;
}

/* 響應式設計 */
@media (max-width: 768px) {
    .roles-section :deep(.ant-col-12) {
        flex: 0 0 100% !important;
        max-width: 100% !important;
    }

    .vaporwave-checkbox-group :deep(.ant-checkbox-wrapper) {
        padding: 6px 8px !important;
        font-size: 13px !important;
    }
}
</style>
